/**
 * lh-image-preload.js
 * Lolahome — Adianta o carregamento das fotos da grade de produtos (Horizon)
 *
 * As fotos de cada card do grid usam loading="lazy" nativo do navegador, que só
 * começa a buscar a imagem quando o card já está bem perto/dentro da tela — numa
 * rolagem rápida isso faz a foto aparecer com atraso visível ("pop-in").
 *
 * Não mexemos no atributo loading="lazy" em si (continua controlando quando o
 * navegador de fato RENDERIZA/decodifica a imagem no <img> real). Em vez disso,
 * "aquecemos" o cache do navegador um pouco antes disso acontecer: quando o card
 * se aproxima da tela (margem configurável abaixo), criamos uma Image() de
 * memória com o mesmo src/srcset, o que dispara o download antecipado. Quando o
 * <img> real finalmente entra em vista e o navegador o carrega "de verdade", a
 * URL já está no cache HTTP e aparece instantaneamente.
 *
 * Não altera o carregamento inicial da página (as mesmas 5 primeiras fotos
 * continuam eager, o resto continua lazy) — só antecipa o download de cada foto
 * lazy um pouco mais cedo durante a rolagem.
 */

const PRELOAD_MARGIN = '600px 0px';
const SELECTOR = '.product-grid img[loading="lazy"]';

/** @param {HTMLImageElement} img */
function warm(img) {
  if (img.dataset.lhWarmed) return;
  if (img.complete && img.naturalWidth > 0) return;
  img.dataset.lhWarmed = '1';

  const preload = new Image();
  const sizes = img.getAttribute('sizes');
  const srcset = img.getAttribute('srcset');

  if (srcset) {
    if (sizes) preload.sizes = sizes;
    preload.srcset = srcset;
  } else {
    preload.src = img.src;
  }
}

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      warm(/** @type {HTMLImageElement} */ (entry.target));
      observer.unobserve(entry.target);
    }
  },
  { rootMargin: PRELOAD_MARGIN }
);

/** @param {ParentNode} root */
function observeAll(root) {
  root.querySelectorAll(SELECTOR).forEach((img) => observer.observe(img));
}

observeAll(document);

// Novos cards entram via infinite scroll / paginação AJAX da grade de coleção —
// precisamos observar as imagens que chegam depois do carregamento inicial.
const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(SELECTOR)) observer.observe(node);
      node.querySelectorAll?.(SELECTOR).forEach((img) => observer.observe(img));
    }
  }
});

mutationObserver.observe(document.body, { childList: true, subtree: true });
